import React, { Fragment } from "react";

import ViewSection from "./ViewSection";
import { faCube, faIdCardAlt, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { TitledContent } from "./TitledContent";
import { IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Renderer } from "../descriptors/common";
import ResourceFormButton from "./ResourceFormButton";
import client from "../descriptors/client";
import DeleteResourceButton from "./DeleteResourceButton";
import ClientRoleMappingRuleDialog from "../utils/ClientRoleMappingRuleDialog";
import { FormattedMessage } from "react-intl";
import NotSetValue from "./NotSetValue";

const RuleButton: React.FC<{
    ruleId?: number;
    action: "create" | "update";
    roles: any[];
}> = ({ ruleId, action, roles }) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const revalidator = useRevalidator();

    return (
        <>
            <IconButton
                onClick={() => {
                    setDialogOpen(true);
                }}
                sx={
                    action === "create"
                        ? {
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              "&:hover": {
                                  bgcolor: "primary.dark",
                              },
                          }
                        : {
                              color: "primary.main",
                          }
                }
            >
                <FontAwesomeIcon icon={action == "create" ? faPlus : faPencil} />
            </IconButton>
            <ClientRoleMappingRuleDialog
                ruleId={ruleId}
                roles={roles}
                open={dialogOpen}
                onHide={(changed) => {
                    if (changed) revalidator.revalidate();
                    setDialogOpen(false);
                }}
            />
        </>
    );
};

const BasicTable: React.FC<{
    data: any;
    fields: string[];
    renderers: Record<string, Renderer>;
    fieldNameKeyFactory: (key: string) => string;
}> = ({ data, fields, renderers, fieldNameKeyFactory }) => {
    let rows = [];
    for (let name of fields) {
        let value = data[name];
        if (name in renderers) value = renderers[name](value, data);
        let nameKey = fieldNameKeyFactory(name);
        rows.push(
            <Fragment key={nameKey}>
                <Grid size={4}>
                    <FormattedMessage id={nameKey} />
                </Grid>
                <Grid size={8}>{value}</Grid>
            </Fragment>,
        );
    }

    return (
        <Grid container spacing={2}>
            {...rows}
        </Grid>
    );
};

const ContextSubstitutions: React.FC<{ data: any }> = ({ data }) => {
    let rows = [];
    for (let row of data) {
        for (let field in row.substitution) {
            rows.push(
                <Fragment key={field}>
                    <Grid size={4}>
                        <FormattedMessage id={field} />
                    </Grid>
                    <Grid size={8}>{row.substitution[field]}</Grid>
                </Fragment>,
            );
        }
    }
    return rows.length ? (
        <Grid container spacing={2}>
            {...rows}
        </Grid>
    ) : null;
};

const ClientView: React.FC<{}> = () => {
    const renderers = client.renderers?.read;
    const resourceName = "client";
    const fieldNameKeyFactory = (fieldName: string) => `${resourceName}.${fieldName}`;
    const data = useLoaderData() as any;
    console.log("loader data", data);

    let content: string | JSX.Element = "";

    function renderRuleTable() {
        let rows = [];
        for (let role of data.roles) {
            for (let rule of role["client_role_mapping_rules"]) {
                rows.push(
                    <TableRow key={rule.id}>
                        <TableCell>{rule.pattern}</TableCell>
                        <TableCell>{role.name}</TableCell>
                        <TableCell>
                            <ContextSubstitutions data={rule["context_substitutions"]} />
                        </TableCell>
                        <TableCell>
                            <RuleButton ruleId={rule.id} action="update" roles={data.roles} />{" "}
                            <DeleteResourceButton resourceName="client_role_mapping_rule" id={rule.id} />
                        </TableCell>
                    </TableRow>,
                );
            }
        }

        if (rows.length == 0) return null;

        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id="client_role_mapping_rule.pattern" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="client_role_mapping_rule.role" />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id="client_role_mapping_rule.substitutions" />
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>{...rows}</TableBody>
            </Table>
        );
    }

    function renderRoleMapperSection() {
        let table = renderRuleTable();

        return (
            <ViewSection
                icon={faIdCardAlt}
                title="client.roleRuleSettingsTitle"
                button={<RuleButton action="create" roles={data.roles} />}
            >
                <div>{table || <NotSetValue label="client.no_rules" />}</div>
            </ViewSection>
        );
    }

    function renderSections() {
        const basicDataFields = ["identifier", "secret", "refresh_token_expires_in", "redirect_uris"];
        if (window.features.ldap) {
            basicDataFields.push("ldap_auth");
        }

        return (
            <Stack spacing={5} mt={5}>
                <ViewSection
                    icon={faCube}
                    title="user.basic_data"
                    button={
                        <ResourceFormButton
                            resourceName="client"
                            action="update"
                            id={data.id}
                            buttonType="section-icon"
                        />
                    }
                >
                    <BasicTable
                        data={data}
                        fields={basicDataFields}
                        renderers={renderers}
                        fieldNameKeyFactory={fieldNameKeyFactory}
                    />
                </ViewSection>
                {data["ldap_auth"] && renderRoleMapperSection()}
            </Stack>
        );
    }

    if (global.identityProvider == "internal") {
        content = renderSections();
    } else {
        content = (
            <BasicTable
                data={data}
                fields={["secret"]}
                renderers={renderers}
                fieldNameKeyFactory={fieldNameKeyFactory}
            />
        );
    }

    return <TitledContent title={data["display_name"]}>{content}</TitledContent>;
};

export default ClientView;
